/**
 * Моя персона (§5).
 *
 * Три блока со статусами. Пока идёт проверка записи (§7) — показываем это
 * честно; при отказе — конкретную причину и что исправить, а не общий список
 * правил заново (agents/web-persona-capture.md §5).
 */
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Video } from 'lucide-react';
import type { CaptureIssue } from '@alterium/shared';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card, CardBody } from '../../components/ui/Card';
import { EmptyState } from '../../components/ui/EmptyState';
import { PageHeader } from '../../components/ui/PageHeader';
import { StatusDot } from '../../components/ui/StatusDot';
import { personaApi } from '../../api/persona';
import { avatarApi } from '../../api/avatar';
import { voiceApi } from '../../api/voice';
import { VoiceCard } from '../../components/persona/VoiceCard';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../i18n/errors';
import { i18n } from '../../i18n';

/** Статус — данные с сервера; перевод подставляется при отрисовке. */
const statusKey = (status: string): string => `enum.personaStatus.${status}`;

/**
 * Подсказка под состоянием аватара.
 *
 * При отказе показываем причину на языке интерфейса: у сервера текст один,
 * а языков десять. Первый настоящий пользователь на отказе прочитал
 * «попробуйте записать видео ещё раз» — совет, который в его случае
 * не помог бы, потому что дело было не в записи.
 *
 * Сервер уже решил, какой код стоит показывать: в подсказке он присылает
 * либо текст причины, либо общую строку состояния. Нам остаётся перевести
 * ровно то же самое.
 */
const HINT_CODES = new Set([
  'CAPTURE_TOO_LARGE',
  'INPUT_QUALITY_LOW',
  'IDENTITY_ASSET_REQUIRED',
  'CONTENT_REJECTED',
  'INSUFFICIENT_UNITS',
]);

const avatarHint = (profile: { status: string; errorCode: string | null; hint: string }): string =>
  profile.status === 'failed' && profile.errorCode && HINT_CODES.has(profile.errorCode)
    ? i18n.t(`error.${profile.errorCode}`, { defaultValue: profile.hint })
    : profile.hint;

export function PersonaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const persona = useQuery({
    queryKey: ['persona'],
    queryFn: personaApi.get,
    // Пока идёт проверка, статус меняется сам — опрашиваем чаще.
    refetchInterval: (query) => (query.state.data?.status === 'checking' ? 3000 : false),
    refetchOnWindowFocus: true,
  });

  const appearance = useQuery({
    queryKey: ['appearance'],
    queryFn: personaApi.appearance,
    enabled: (persona.data?.appearanceCount ?? 0) > 0,
  });

  /** §22: готова ли персона к динамичным сценам. */
  const prep = useQuery({
    queryKey: ['dynamic-prep'],
    queryFn: personaApi.dynamicPrep,
    // Пока идёт разбор записей, состояние меняется само.
    refetchInterval: (query) => (query.state.data?.processing ? 5000 : false),
  });

  const avatar = useQuery({
    queryKey: ['avatar'],
    queryFn: avatarApi.get,
    // Создание аватара идёт долго (§8): опрашиваем, пока он в работе.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && !['not_started', 'ready', 'failed', 'consent_required'].includes(status)
        ? 5000
        : false;
    },
    refetchOnWindowFocus: true,
  });

  const voice = useQuery({
    queryKey: ['voice'],
    queryFn: voiceApi.get,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'analyzing' || status === 'cloning' ? 4000 : false;
    },
    refetchOnWindowFocus: true,
  });

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['avatar'] }),
      queryClient.invalidateQueries({ queryKey: ['persona'] }),
    ]);

  const startAvatar = useMutation({ mutationFn: avatarApi.start, onSuccess: refresh });
  const removeAvatar = useMutation({ mutationFn: avatarApi.remove, onSuccess: refresh });

  const data = persona.data;
  const check = data?.lastCheck;
  const failures = (check?.issues ?? []).filter((issue) => issue.severity === 'fail');
  const warnings = (check?.issues ?? []).filter((issue) => issue.severity === 'warn');

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title={t('persona.title')}
        description={t('persona.subtitle')}
        action={
          data?.hasCapture ? (
            <Button onClick={() => navigate('/persona/capture')}>{t('persona.recapture')}</Button>
          ) : null
        }
      />

      {!data?.hasCapture ? (
        <EmptyState
          title={t('persona.empty.title')}
          description={t('persona.empty.description')}
          action={
            <Button variant="primary" onClick={() => navigate('/persona/capture')}>
              <Video className="h-4 w-4" strokeWidth={2} />
              {t('home.createPersona')}
            </Button>
          }
        />
      ) : (
        <>
          {data.status === 'checking' ? <Alert>{t('persona.checkingNotice')}</Alert> : null}

          {data.status === 'rejected' ? (
            <div className="flex flex-col gap-4">
              <Alert tone="error">
                {t('persona.rejected')} {failures[0]?.message ?? t('persona.tryRecordAgain')}
              </Alert>
              {failures.length > 1 ? <IssueList issues={failures.slice(1)} /> : null}
              <div>
                <Button variant="primary" onClick={() => navigate('/persona/capture')}>
                  {t('persona.recordAgain')}
                </Button>
              </div>
            </div>
          ) : null}

          {data.status !== 'rejected' && warnings.length > 0 ? (
            <Card>
              <CardBody className="flex flex-col gap-3">
                <p className="text-card-title">{t('persona.accepted')}</p>
                <p className="text-body text-content-secondary">{t('persona.acceptedWithNotes')}</p>
                <IssueList issues={warnings} />
              </CardBody>
            </Card>
          ) : null}

          <section>
            <h2 className="mb-4 text-section-title font-medium">{t('persona.state')}</h2>
            <Card>
              <CardBody className="flex flex-wrap items-center gap-x-8 gap-y-3">
                {data.parts.map((part) => (
                  <div key={part.key} className="flex items-center gap-2 text-body">
                    <StatusDot tone={part.ready ? 'ok' : 'idle'} />
                    <span className="text-content">{part.label}</span>
                    <span className="text-content-muted">{part.status}</span>
                  </div>
                ))}
                <span className="ml-auto text-meta text-content-muted">
                  {t(statusKey(data.status), { defaultValue: data.status })}
                </span>
              </CardBody>
            </Card>
          </section>

          {/* §8: аватар — отдельный блок со своим статусом и действием */}
          {data.status !== 'rejected' && avatar.data ? (
            <section>
              <h2 className="mb-4 text-section-title font-medium">{t('persona.part.avatar')}</h2>
              <Card>
                <CardBody className="flex flex-col gap-4">
                  <div className="flex items-start gap-4">
                    {avatar.data.previewUrl ? (
                      <div className="h-24 w-16 shrink-0 overflow-hidden rounded-control bg-surface-secondary">
                        <img
                          src={avatar.data.previewUrl}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <StatusDot
                          tone={
                            avatar.data.status === 'ready'
                              ? 'ok'
                              : avatar.data.status === 'failed'
                                ? 'fail'
                                : avatar.data.status === 'not_started'
                                  ? 'idle'
                                  : 'warn'
                          }
                        />
                        <span className="text-body text-content">{avatar.data.label}</span>
                      </div>
                      <p className="mt-1 text-body text-content-secondary">
                        {avatarHint(avatar.data)}
                      </p>
                    </div>
                  </div>

                  {startAvatar.isError ? (
                    <Alert tone="error">{errorText(startAvatar.error)}</Alert>
                  ) : null}

                  <div className="flex flex-wrap gap-3">
                    {avatar.data.consentRequired ? (
                      <Button variant="primary" onClick={() => navigate('/persona/consent')}>
                        {t('persona.confirmIdentity')}
                      </Button>
                    ) : null}

                    {avatar.data.status === 'not_started' || avatar.data.status === 'failed' ? (
                      <Button
                        variant="primary"
                        onClick={() => startAvatar.mutate()}
                        disabled={startAvatar.isPending}
                      >
                        {avatar.data.status === 'failed'
                          ? t('common.tryAgain')
                          : t('persona.createAvatar')}
                      </Button>
                    ) : null}

                    {avatar.data.status === 'ready' ? (
                      <Button
                        className="border-error/50 text-error hover:border-error"
                        onClick={() => removeAvatar.mutate()}
                        disabled={removeAvatar.isPending}
                      >
                        {t('persona.deleteAvatar')}
                      </Button>
                    ) : null}
                  </div>

                  {avatar.data.status === 'ready' ? (
                    <p className="text-meta text-content-muted">
                      {t('persona.avatarDeleteWarning')}
                    </p>
                  ) : null}
                </CardBody>
              </Card>
            </section>
          ) : null}

          {/* §10: голос — из той же записи, отдельной записи голоса нет */}
          {data.status !== 'rejected' && voice.data ? (
            <section>
              <h2 className="mb-4 text-section-title font-medium">{t('persona.part.voice')}</h2>
              <VoiceCard voice={voice.data} />
            </section>
          ) : null}

          {data.appearanceCount > 0 ? (
            <section>
              <h2 className="mb-1 text-section-title font-medium">
                {t('persona.part.appearance')}
              </h2>
              <p className="mb-4 text-body text-content-secondary">{t('persona.appearanceNote')}</p>

              {/*
                §22: подготовка к динамичным сценам.

                Предлагаем, а не требуем: сцена сама попросит её, когда
                понадобится. Но человек, который уже знает, чего хочет,
                должен иметь возможность сделать это заранее.
              */}
              {prep.data ? (
                <div className="mb-4 flex flex-wrap items-center gap-3 rounded-card border border-line bg-surface p-4">
                  <p className="min-w-0 flex-1 text-body text-content-secondary">
                    {prep.data.processing
                      ? t('prep.processing')
                      : prep.data.ready
                        ? t('prep.readyHint', { count: prep.data.frames })
                        : t('prep.subtitle')}
                  </p>
                  {prep.data.processing ? null : (
                    <Button onClick={() => navigate('/persona/dynamic')}>
                      {/* Готовую подготовку можно переснять: сменилась внешность,
                          был неудачный свет — другого пути к этой съёмке нет. */}
                      {prep.data.ready ? t('prep.again') : t('persona.prepareDynamic')}
                    </Button>
                  )}
                </div>
              ) : null}

              <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
                {(appearance.data ?? []).map((item) => (
                  <div
                    key={item.id}
                    className="overflow-hidden rounded-card border border-line bg-surface-secondary"
                  >
                    <div className="aspect-[3/4]">
                      {item.url ? (
                        <img
                          src={item.url}
                          alt=""
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}

function IssueList({ issues }: { issues: CaptureIssue[] }) {
  return (
    <ul className="flex flex-col gap-2">
      {issues.map((issue) => (
        <li key={issue.code} className="flex gap-2.5 text-body text-content-secondary">
          <StatusDot tone={issue.severity === 'fail' ? 'fail' : 'warn'} className="mt-2" />
          {issue.message}
        </li>
      ))}
    </ul>
  );
}
